import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'
import { nextAsk, type Ask, type Profile } from '../completeness'

/*
  The standing ask for the one piece of profile that would most improve what the board says.

  It sits in the shell rather than on the company screen, because the owner who has not
  filled the profile in is by definition not on the company screen. It is the same reason it
  cannot be dismissed permanently: a board answering generically is a worse product every
  week it goes unfixed, and "no thanks" to that is almost always "not right now".

  So the close button snoozes for three days rather than silencing. It comes back, it asks
  for one thing, and it says what that thing changes.
*/

const SNOOZE_DAYS = 3
const KEY = 'boardium.nudge.snoozed'

type Snoozes = Record<string, number>

function read(): Snoozes {
  try {
    // Private windows, cleared site data and browsers set to block storage all land here.
    // A nudge that throws on boot would take the whole shell down with it.
    return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Snoozes
  } catch {
    return {}
  }
}

function write(value: Snoozes) {
  try { localStorage.setItem(KEY, JSON.stringify(value)) } catch { /* not important enough to care */ }
}

export function ProfileNudge({ onNavigate }: { onNavigate: (screen: 'company' | 'numbers') => void }) {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [snoozes, setSnoozes] = useState<Snoozes>(read)

  const load = useCallback(() => {
    void api.get<Profile>('/api/company').then(setProfile).catch(() => setProfile(null))
  }, [])

  useEffect(load, [load])

  /*
    Re-read when the tab is brought back. Somebody who follows this to the company screen,
    fills the field in and comes back should not still be looking at the ask, and the shell
    has no other reason to know the profile changed.
  */
  useEffect(() => {
    const on = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', on)
    return () => document.removeEventListener('visibilitychange', on)
  }, [load])

  const isSnoozed = (id: string) => (snoozes[id] ?? 0) > Date.now()
  const ask: Ask | null = nextAsk(profile, isSnoozed)
  if (!ask) return null

  const snooze = () => {
    const next = { ...snoozes, [ask.id]: Date.now() + SNOOZE_DAYS * 86_400_000 }
    setSnoozes(next)
    write(next)
  }

  return (
    <aside className="nudge" role="status">
      <div className="nudge__body">
        <p className="nudge__label">{ask.label}</p>
        <p className="nudge__why">{ask.why}</p>
      </div>

      <div className="nudge__actions">
        {/*
          Navy outline, not the gold fill.

          This button was gold on all nine screens and was the only gold on eight of them,
          which made the loudest colour in the brand belong to a dismissible prompt rather
          than to whatever the screen is actually for. Gold now marks the primary action and
          nothing else, so on Home it means "ask the board" and on a screen with no primary
          action it does not appear at all.
        */}
        <button className="btn btn--ghost btn--sm" onClick={() => onNavigate(ask.screen)}>
          {ask.cta}
        </button>
        <button className="linkish nudge__later" onClick={snooze}>Not now</button>
      </div>
    </aside>
  )
}
